// Phase 7 — Track B T-B2. FormError primitive.
//
// Renders a single field's RHF error message with the accessibility
// affordances the plan §2.10 calls out:
//
//   * role="alert" — assistive tech announces the error immediately
//   * aria-live="polite" — non-interruptive (per blueprint §13.4 calm tone)
//   * danger color comes from the `state-danger` token (color.ts) — no raw hex
//
// FormError is rendered automatically by FormField when the field's RHF
// state has an error. Consumers may also render it directly for non-field
// (form-level) errors.

import * as React from 'react';
import { useFormContext, type FieldErrors } from 'react-hook-form';
import { cn } from '../../lib/cn.js';

export interface FormErrorProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Field name (RHF path). When omitted, no error is rendered (use a
   *  manual children prop for form-level errors). */
  name?: string;
  /** Manual override — render whatever the consumer passes, regardless
   *  of RHF state. Useful for form-level (non-field) errors. */
  children?: React.ReactNode;
}

/**
 * Drill into RHF's nested error tree using a dotted path (e.g.
 * `reminders.0.offset_days`). Returns `undefined` when nothing is set.
 */
function lookupError(errors: FieldErrors, name: string): { message?: string } | undefined {
  const parts = name.split('.');
  // RHF errors is a tree of { type, message, ref, ... } leaves keyed by
  // path. We walk it as `unknown` because the leaf type is the inner
  // FieldError (which has `message`) and intermediates are objects.
  let cursor: unknown = errors;
  for (const p of parts) {
    if (cursor === null || cursor === undefined || typeof cursor !== 'object') {
      return undefined;
    }
    cursor = (cursor as Record<string, unknown>)[p];
  }
  if (cursor && typeof cursor === 'object' && 'message' in cursor) {
    return cursor as { message?: string };
  }
  return undefined;
}

export const FormError = React.forwardRef<HTMLParagraphElement, FormErrorProps>(
  ({ name, children, className, ...props }, ref) => {
    // useFormContext returns null when the FormError is rendered outside a
    // <FormProvider>. We tolerate that for form-level errors (no name +
    // explicit children).
    const ctx = useFormContext();

    // Manual override path (form-level error).
    if (children !== undefined && children !== null && children !== false) {
      return (
        <p
          ref={ref}
          role="alert"
          aria-live="polite"
          className={cn('text-caption text-state-danger', className)}
          {...props}
        >
          {children}
        </p>
      );
    }

    if (!name || !ctx) return null;
    const err = lookupError(ctx.formState.errors, name);
    if (!err || !err.message) return null;

    return (
      <p
        ref={ref}
        role="alert"
        aria-live="polite"
        className={cn('text-caption text-state-danger', className)}
        {...props}
      >
        {err.message}
      </p>
    );
  },
);
FormError.displayName = 'FormError';
